import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { datadogLogs } from '@datadog/browser-logs';

import { useApi } from './withApi';
import transform from './transform';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from './auth0';
import { isFusionNonHurricane } from './policyType';
export const PolicyContext = React.createContext({});

export const usePolicy = (options = {}) => {
  const navigate = useNavigate();
  const context = useContext(PolicyContext);
  const [error, setError] = useState(null);
  const onError = useCallback((err, entity) => setError({ err, entity }), []);
  const [searchParams] = useSearchParams();
  const selectedPolicyNumber = searchParams.get('policy') || null;
  const { user } = useAuth0();

  const {
    fetchPolicy,
    selectedPolicy,
    setLoading,
    fetchPolicies,
    policies,
    claims,
    initialLoad,
    setInitialLoad,
  } = context;
  const loadPolicies = options?.loadPolicies || false;
  const policyNumber =
    user?.role?.policies?.length > 0 ? user?.role?.policies[0] : null;

  useEffect(() => {
    if (loadPolicies && !policies && setInitialLoad && policyNumber) {
      setInitialLoad(true);
      setLoading?.(true);
      fetchPolicies?.(onError, policyNumber);
    }
  }, [policies]);

  useEffect(() => {
    if (initialLoad && policies && selectedPolicy && claims) {
      setInitialLoad?.(false);
      setLoading?.(false);
    }
  }, [claims, initialLoad, policies, selectedPolicy]);

  useEffect(() => {
    if (
      selectedPolicyNumber &&
      (!selectedPolicy || selectedPolicy.policyNumber !== selectedPolicyNumber)
    ) {
      fetchPolicy?.(selectedPolicyNumber, navigate, onError);
    }
  }, [selectedPolicyNumber]);

  return { ...context, error, setError };
};

export const PolicyProvider = ({ children }) => {
  const [policies, setPolicies] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [selectedPolicyFusionNH, setSelectedPolicyFusionNH] = useState(null);
  const [loading, setLoading] = useState(false);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [claims, setClaims] = useState([]);
  const [initialLoad, setInitialLoad] = useState(false);
  const api = useApi();

  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  const inFlightPolicyRef = useRef(null);
  const inFlightPolicyPromiseRef = useRef(null);

  const fetchClaims = useCallback(
    (policy, onError) => {
      setClaimsLoading(true);

      const fusionData = {
        policyNumber: policy?.policyNumber,
        isFusion: policy?.isFusion,
        type: policy?.type,
      };

      api.withAuth
        .getClaims(policy?.policyNumber)
        .then((res) => {
          if (res.status === 200) {
            const transClaims = transform.claims(res.data, fusionData);
            setClaims(transClaims);
          }
          if (!policy?.isFusion) {
            setClaimsLoading(false);
          }
        })
        .catch((err) => {
          datadogLogs.logger.error(`Fetch Claims - ${policy?.policyNumber}`, {
            err,
          });
          if (!policy?.isFusion) {
            setClaimsLoading(false);
          }
          if (onError) {
            onError(err, 'claims');
          }
        });

      if (policy?.isFusion) {
        const nonHurricaneData = {
          policyNumber: `${policy?.policyNumber}N`,
          isFusion: true,
          type: 'Homeowners Surplus',
        };

        api.withAuth
          .getClaims(`${policy?.policyNumber}N`)
          .then((res) => {
            if (res.status === 200) {
              const transClaims = transform.claims(res.data, nonHurricaneData);
              setClaims((prev) => [...(prev ?? []), ...transClaims]);
            }
            setClaimsLoading(false);
          })
          .catch((err) => {
            datadogLogs.logger.error(
              `Fetch Claims - ${policy?.policyNumber}N`,
              {
                err,
              },
            );
            setClaimsLoading(false);
            if (onError) {
              onError(err, 'claims');
            }
          });
      }
    },
    [api.withAuth],
  );

  const fetchPolicy = useCallback(
    (policyNumber, navigate, onError, options = {}) => {
      const force = options?.force === true;

      if (!policyNumber) {
        return Promise.resolve(null);
      }
      const isNewPolicy = selectedPolicy?.policyNumber !== policyNumber;

      if (
        !force &&
        selectedPolicy?.policyNumber === policyNumber &&
        !loadingRef.current &&
        inFlightPolicyRef.current !== policyNumber
      ) {
        return Promise.resolve(selectedPolicy);
      }

      if (
        inFlightPolicyRef.current === policyNumber &&
        inFlightPolicyPromiseRef.current
      ) {
        return inFlightPolicyPromiseRef.current;
      }
      if (!loadingRef.current) {
        setLoading(true);
        if (isNewPolicy) setClaims(null);
      }

      inFlightPolicyRef.current = policyNumber;

      const request = api.withAuth
        .getPolicy(policyNumber)
        .then((res) => {
          const transPolicy = transform.policy(res.data);
          setSelectedPolicy(transPolicy);

          if (
            isNewPolicy &&
            (!transPolicy.isFusion || !isFusionNonHurricane(transPolicy))
          ) {
            fetchClaims?.(transPolicy, onError);
          }

          if (transPolicy.isFusion) {
            const fusionNHNumber = `${policyNumber}N`;
            return api.withAuth
              .getPolicy(fusionNHNumber)
              .then((fusionNHRes) => {
                const transFusionNH = transform.policy(fusionNHRes.data);
                setSelectedPolicyFusionNH(transFusionNH);
                setLoading(false);
                return transPolicy;
              })
              .catch((fusionNHErr) => {
                datadogLogs.logger.error(
                  `Fetch Policy Fusion Non Hurricane - ${fusionNHNumber}`,
                  { err: fusionNHErr },
                );
                setSelectedPolicyFusionNH(null);
                setLoading(false);
                if (onError) {
                  onError(fusionNHErr, 'Non Hurricane policy');
                }
                return transPolicy;
              });
          }

          setSelectedPolicyFusionNH(null);
          setLoading(false);
          return transPolicy;
        })
        .catch((err) => {
          if (err.response && err.response.status === 503) {
            onError({ status: 503 }, 'policy');
            setLoading(false);
            return null;
          }
          if (navigate && err.response && err.response.status === 403) {
            if (onError) {
              onError(
                { response: { statusText: 'Policy not related to the user' } },
                'policy',
              );
            }
            setLoading(false);
            navigate(`/`, { replace: true });
            return null;
          }
          datadogLogs.logger.error(`Fetch Policy - ${policyNumber}`, {
            err,
          });
          if (onError) {
            onError(err, 'policy');
          }
          setLoading(false);
          return null;
        })
        .finally(() => {
          if (inFlightPolicyRef.current === policyNumber) {
            inFlightPolicyRef.current = null;
            inFlightPolicyPromiseRef.current = null;
          }
        });

      inFlightPolicyPromiseRef.current = request;
      return request;
    },
    [loadingRef, setLoading, setClaims, api, setSelectedPolicy, selectedPolicy],
  );

  const fetchPolicies = (onError, policyNumber) => {
    api.withAuth
      .getAllPolicies(policyNumber)
      .then((res) => {
        if (res.data.length === 0) {
          setLoading(false);
          setPolicies([]);
          return;
        }

        const transformedPolicies = transform.policies(res.data);

        const latestByPolicyNumber = new Map();
        transformedPolicies.forEach((policy) => {
          const existing = latestByPolicyNumber.get(policy.policyNumber);
          if (!existing) {
            latestByPolicyNumber.set(policy.policyNumber, policy);
            return;
          }

          const isNewerEffective =
            new Date(policy.effectiveDate) > new Date(existing.effectiveDate);
          const isNewerExpiration =
            new Date(policy.expirationDate) > new Date(existing.expirationDate);

          if (isNewerEffective && isNewerExpiration) {
            latestByPolicyNumber.set(policy.policyNumber, policy);
          }
        });

        const latestPolicies = Array.from(latestByPolicyNumber.values());

        setLoading(false);
        setPolicies(latestPolicies);
      })
      .catch((err) => {
        if (err.response && err.response.status === 503) {
          onError({ status: 503 }, 'policy');
          return null;
        }
        setLoading(false);
        if (onError) {
          datadogLogs.logger.error(`Fetch Policies`, {
            err,
          });
          onError(err, 'policies');
        }
        setPolicies([]);
      });
  };
  return (
    <PolicyContext.Provider
      value={{
        policies,
        selectedPolicy,
        selectedPolicyFusionNH,
        claims,
        setPolicies,
        setSelectedPolicy,
        setSelectedPolicyFusionNH,
        setClaims,
        fetchPolicy,
        fetchPolicies,
        fetchClaims,
        claimsLoading,
        loading,
        setLoading,
        initialLoad,
        setInitialLoad,
      }}
    >
      {children}
    </PolicyContext.Provider>
  );
};

PolicyProvider.propTypes = {
  children: PropTypes.any,
};
